import dynamic from 'next/dynamic';
import {ReactNode, useState, useEffect, FC} from 'react';
import Loader from '@jobseeker/lotus/components/Loader';
import {
  HOME,
  JOBDETAIL,
  EPOCH,
  COURSE,
  COMPANY,
  TAG,
  SEARCH,
  JOBFEED,
  SITEMAP,
} from '@constants';
import {shallowEqual, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import ScrollToTop from '@jobseeker/lotus/components/ScrollToTop';
import {Box, useTheme} from '@mui/material';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import useInfoBannerHeight from '@jobseeker/common-core/hooks/useInfoBannerHeight';
import {utilServerSideDeviceDetection} from '@utils';
import Promotions from '@jobseeker/feature-ads/components/Promotions';
import {useIsCustomWidth} from '@jobseeker/lotus/hooks';
import HiristPromotions from '@jobseeker/feature-ads/components/HiristPromotions';
import cookie from 'cookie';
import {getItemWithExpiry} from '@jobseeker/common-core/utils';

const HeaderAuth = dynamic(
  () =>
    import('@jobseeker/client-harness/components/NavigationHarness/HeaderAuth'),
  {
    loading: () => <Loader />,
  }
);

const HeaderNonAuth = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/HeaderNonAuth'
    ),
  {
    loading: () => <Loader />,
  }
);

const CookieBanner = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/CookieBanner'
    ),
  {
    loading: () => <Loader />,
  }
);

const Footer = dynamic(
  () => import('@jobseeker/client-harness/components/NavigationHarness/Footer'),
  {
    loading: () => <Loader />,
  }
);

const HeaderProfile = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/HeaderProfile'
    ),
  {
    loading: () => <Loader />,
  }
);

interface LayoutProps {
  page?: string;
  meta?: ReactNode;
  children: ReactNode;
  HeaderComp?: FC;
  showbottom?: boolean;
}

const NavLayout = ({HeaderComp, ...props}: LayoutProps) => {
  const theme = useTheme();
  const bannerHeight = useInfoBannerHeight();
  const isLargeDevice = useIsLargeDevice();
  const isAboveXL = useIsLargeDevice(1442);
  const isSmall = useIsCustomWidth(500);
  const [promotionType, setPromotionType] = useState('');
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const currentPage = useSelector(
    (state: AppState) => state.app.page,
    shallowEqual
  );

  const {children, page} = props || {
    page: 'all',
  };

  const cookieBannerData = {
    policyLinks: {
      privacyPolicy: 'https://www.iimjobs.com/privacy/',
      cookiePolicy: 'https://www.iimjobs.com/cookiepolicy',
    },
    CTA: 'Got it',
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const userAgentInfo = utilServerSideDeviceDetection({});
      sessionStorage.setItem(
        'device',
        userAgentInfo.isMobile ? 'mobile' : 'web'
      );
      sessionStorage.setItem('deviceType', userAgentInfo.deviceType || 'web');
    }
  }, []);
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const [showHiristPromotionBanner, setShowHiristPromotionBanner] =
    useState(false);

  const [url, setUrl] = useState('');
  const [cookieValue, setCookieValue] = useState('');
  const [hideBanner, setHideBanner] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    const cookies = cookie.parse(document?.cookie);
    const myCookieValue = cookies['linkage_banner'];
    setCookieValue(myCookieValue);
    const bannerData = getItemWithExpiry('HIDE_APP_PROMO_BANNER');
    setHideBanner(bannerData);
  }, []);

  useEffect(() => {
    if (
      (currentPage === TAG ||
        currentPage === SEARCH ||
        currentPage === JOBDETAIL ||
        (currentPage === JOBFEED &&
          !url.includes('/company') &&
          !url.includes('/courses'))) &&
      cookieValue != '1' &&
      isSmall &&
      !isAppIimjobs
    ) {
      setShowHiristPromotionBanner(true);
    } else {
      setShowHiristPromotionBanner(false);
    }
  }, [currentPage, url, isSmall]);

  const handleNewLinkageBanner = () => {
    setShowHiristPromotionBanner(false);
  };

  return (
    <>
      {!isAppIimjobs && isSmall && showHiristPromotionBanner && (
        <HiristPromotions onLinkageCrossClick={handleNewLinkageBanner} />
      )}
      {page === HOME ? (
        <HeaderNonAuth />
      ) : page === EPOCH ? (
        <HeaderProfile />
      ) : HeaderComp ? (
        <HeaderComp />
      ) : (
        <HeaderAuth showHiristPromotionBanner={showHiristPromotionBanner} />
      )}
      <Box
        pt={
          bannerHeight / 8 +
          (showHiristPromotionBanner
            ? currentPage === JOBDETAIL
              ? 12
              : 10
            : 0)
        }
      />
      {children}
      {currentPage !== JOBDETAIL &&
        currentPage !== COURSE &&
        currentPage !== COMPANY &&
        !(currentPage?.indexOf('epoch') > -1) &&
        !page?.includes('registration-') &&
        isSmall &&
        !showCookieBanner && (
          <Promotions
            theme={theme}
            showbottom={props.showbottom}
            setPromotionType={setPromotionType}
            style={{
              position: 'fixed',
              width: '100%',
              paddingLeft: theme.spacing(1.875),
              paddingRight: theme.spacing(1.875),
              bottom: '25px',
              zIndex: 12,
            }}
          />
        )}
      {!page?.includes('registration-') && (
        <ScrollToTop
          theme={theme}
          style={{
            right: isAppIimjobs ? (isAboveXL ? '136px' : '7px') : '0px',
            bottom: !isAppIimjobs
              ? isLargeDevice
                ? '56px'
                : !hideBanner &&
                  currentPage !== COURSE &&
                  currentPage !== COMPANY
                ? '176px'
                : '70px'
              : isLargeDevice
              ? currentPage === JOBDETAIL
                ? '100px'
                : '60px'
              : page === COURSE || currentPage === JOBDETAIL
              ? '150px'
              : promotionType === 'Install Now'
              ? '150px'
              : promotionType === 'Get App'
              ? '160px'
              : '17px',
            position: 'fixed',
            zIndex: 12,
            cursor: 'pointer',
          }}
        />
      )}
      {(isAppIimjobs || currentPage === SITEMAP) && <Footer />}
      {page === HOME && (
        <CookieBanner
          showCookieBanner={showCookieBanner}
          setShowCookieBanner={setShowCookieBanner}
          policyLinks={cookieBannerData.policyLinks}
          CTA={cookieBannerData.CTA}
        />
      )}
    </>
  );
};

export default NavLayout;
